import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface EditSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: any;
  onSuccess: () => void;
}

export function EditSubscriptionDialog({ open, onOpenChange, subscription, onSuccess }: EditSubscriptionDialogProps) {
  const [plan, setPlan] = useState<"basic" | "standard">("basic");
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);
  
  // PTA Details
  const [ptaName, setPtaName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [charityNumber, setCharityNumber] = useState("");
  const [sharedEmail, setSharedEmail] = useState("");

  useEffect(() => {
    if (subscription) {
      setPlan(subscription.plan || "basic");
      setStatus(subscription.status || "active");
      
      // Load PTA details if available
      if (subscription.ptas) {
        setPtaName(subscription.ptas.name || "");
        setSchoolName(subscription.ptas.school_name || "");
        setCharityNumber(subscription.ptas.charity_number || "");
        setSharedEmail(subscription.ptas.shared_email || "");
      }
    }
  }, [subscription]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Update subscription
    const { error: subError } = await supabase
      .from('subscriptions')
      .update({
        plan: plan,
        status: status
      })
      .eq('id', subscription.id);

    if (subError) {
      setLoading(false);
      toast({
        title: "Error",
        description: subError.message,
        variant: "destructive"
      });
      return;
    }

    // Update PTA details if pta_id exists
    if (subscription.pta_id) {
      const { error: ptaError } = await supabase
        .from('ptas')
        .update({
          name: ptaName,
          school_name: schoolName,
          charity_number: charityNumber,
          shared_email: sharedEmail
        })
        .eq('id', subscription.pta_id);

      if (ptaError) {
        setLoading(false);
        toast({
          title: "Error updating PTA",
          description: ptaError.message,
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(false);
    toast({
      title: "Success",
      description: "Subscription and PTA details updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Subscription & PTA</DialogTitle>
          <DialogDescription>Update subscription and PTA details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="subscription" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="pta">PTA Details</TabsTrigger>
            </TabsList>
            
            <TabsContent value="subscription" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={plan} onValueChange={(value) => setPlan(value as "basic" | "standard")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            
            <TabsContent value="pta" className="space-y-4 mt-4">
              {subscription?.pta_id ? (
                <>
                  <div className="space-y-2">
                    <Label>PTA Name</Label>
                    <Input
                      value={ptaName}
                      onChange={(e) => setPtaName(e.target.value)}
                      placeholder="e.g., Hillside Primary PTA"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>School Name</Label>
                    <Input
                      value={schoolName}
                      onChange={(e) => setSchoolName(e.target.value)}
                      placeholder="e.g., Hillside Primary School"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Charity Number (Optional)</Label>
                    <Input
                      value={charityNumber}
                      onChange={(e) => setCharityNumber(e.target.value)}
                      placeholder="e.g., 123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shared Email (Optional)</Label>
                    <Input
                      type="email"
                      value={sharedEmail}
                      onChange={(e) => setSharedEmail(e.target.value)}
                      placeholder="e.g., pta@school.com"
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No PTA associated with this subscription. PTA details can only be edited for subscriptions with linked PTAs.
                </p>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
